// SPDX-License-Identifier: AGPL-3.0-or-later

import DeveloperMode from '@app/features/devtools/state/DeveloperMode';
import type {FeedOutcome} from '@app/features/feed/commands/FeedCommands';
import {FeedShareModal} from '@app/features/feed/components/FeedShareModal';
import {
	COPY_LINK_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {
	CopyIdIcon,
	CopyLinkIcon,
	DeleteIcon,
	EditIcon,
	ReportMessageIcon,
	SendInvitesIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const POST_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'This post is no longer available.',
	comment: 'Toast when acting on a feed post that was deleted or is hidden from you.',
});
const FEED_ACTION_NOT_ALLOWED_DESCRIPTOR = msg({
	message: "You can't do that here.",
	comment: 'Toast when the server refuses a feed action, for example because the feed is off or an account is blocked.',
});
const FEED_INPUT_REFUSED_DESCRIPTOR = msg({
	message: 'Check what you wrote and try again.',
	comment:
		'Toast when a new feed post is refused as it is, for example poll answers that turn out to repeat. Nothing was posted.',
});
const SEND_TO_FRIEND_DESCRIPTOR = msg({
	message: 'Send to a friend',
	comment: 'Menu action on a feed post that opens a friend picker and sends the link of the post to the chosen DM.',
});

// One toast per failed feed action; 'ok' stays silent.
export function toastFeedFailure(i18n: I18n, outcome: FeedOutcome): void {
	if (outcome === 'gone') ToastCommands.error(i18n._(POST_UNAVAILABLE_DESCRIPTOR));
	else if (outcome === 'forbidden') ToastCommands.error(i18n._(FEED_ACTION_NOT_ALLOWED_DESCRIPTOR));
	else if (outcome === 'failed') ToastCommands.error(i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
	else if (outcome === 'invalid') ToastCommands.error(i18n._(FEED_INPUT_REFUSED_DESCRIPTOR));
}

interface FeedItemMenuProps {
	onClose: () => void;
	deleteLabel: MessageDescriptor;
	reportLabel: MessageDescriptor;
	onDelete: (() => void) | null;
	onReport: (() => void) | null;
	copyId?: {id: string; label: MessageDescriptor};
	// Micup: the post page URL, to copy or send to a friend; comments have no page of their own.
	shareUrl?: string;
	// Micup akis-v3: the author's own video post, to replace its cover.
	onChangeCover?: (() => void) | null;
	changeCoverLabel?: MessageDescriptor;
}

// The "..." menu of a post or a comment: delete for whoever may delete it, report for everyone else, and in developer
// mode the item's ID to copy.
export const FeedItemMenu = observer(
	({
		onClose,
		deleteLabel,
		reportLabel,
		onDelete,
		onReport,
		copyId,
		shareUrl,
		onChangeCover,
		changeCoverLabel,
	}: FeedItemMenuProps) => {
		const {i18n} = useLingui();
		const run = (action: () => void) => () => ModalCommands.runAfterBottomSheetClose(onClose, action);
		return (
			<>
				{shareUrl && (
					<MenuGroup data-flx="feed.feed-item-menu.share-menu-group">
						<MenuItem
							icon={<CopyLinkIcon data-flx="feed.feed-item-menu.copy-link-icon" />}
							onClick={run(() => void TextCopyCommands.copy(i18n, shareUrl))}
							data-flx="feed.feed-item-menu.menu-item.copy-link"
						>
							{i18n._(COPY_LINK_DESCRIPTOR)}
						</MenuItem>
						<MenuItem
							icon={<SendInvitesIcon data-flx="feed.feed-item-menu.send-to-friend-icon" />}
							onClick={run(() =>
								ModalCommands.push(
									ModalCommands.modal(() => (
										<FeedShareModal url={shareUrl} data-flx="feed.feed-item-menu.feed-share-modal" />
									)),
								),
							)}
							data-flx="feed.feed-item-menu.menu-item.send-to-friend"
						>
							{i18n._(SEND_TO_FRIEND_DESCRIPTOR)}
						</MenuItem>
					</MenuGroup>
				)}
				<MenuGroup data-flx="feed.feed-item-menu.menu-group">
					{onChangeCover && changeCoverLabel && (
						<MenuItem
							icon={<EditIcon data-flx="feed.feed-item-menu.edit-icon" />}
							onClick={run(onChangeCover)}
							data-flx="feed.feed-item-menu.menu-item.change-cover"
						>
							{i18n._(changeCoverLabel)}
						</MenuItem>
					)}
					{onDelete && (
						<MenuItem
							icon={<DeleteIcon data-flx="feed.feed-item-menu.delete-icon" />}
							onClick={run(onDelete)}
							danger
							data-flx="feed.feed-item-menu.menu-item.delete"
						>
							{i18n._(deleteLabel)}
						</MenuItem>
					)}
					{onReport && (
						<MenuItem
							icon={<ReportMessageIcon data-flx="feed.feed-item-menu.report-message-icon" />}
							onClick={run(onReport)}
							danger
							data-flx="feed.feed-item-menu.menu-item.report"
						>
							{i18n._(reportLabel)}
						</MenuItem>
					)}
				</MenuGroup>
				{copyId && DeveloperMode.isDeveloper && (
					<MenuGroup data-flx="feed.feed-item-menu.developer-menu-group">
						<MenuItem
							icon={<CopyIdIcon data-flx="feed.feed-item-menu.copy-id-icon" />}
							onClick={run(() => void TextCopyCommands.copy(i18n, copyId.id, true))}
							data-flx="feed.feed-item-menu.menu-item.copy-id"
						>
							{i18n._(copyId.label)}
						</MenuItem>
					</MenuGroup>
				)}
			</>
		);
	},
);
